import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import {
  FiChevronDown,
  FiChevronLeft,
  FiX,
  FiCheck,
  FiAlertCircle
} from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import * as Yup from 'yup';
import { addSegment, SegmentConfig } from '../../features/audience/audienceSlice';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from '../../utils/jwt';

// Type-cast icons
const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiCheckComponent = FiCheck as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiAlertCircleComponent = FiAlertCircle as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface FormValues {
  segmentName: string;
  segmentType: 'DYNAMIC' | 'STATIC';
  logicalOperator: 'AND' | 'OR';
  selectedFields: string[];
  fieldTypes: { [key: string]: string };
  fieldOperators: { [key: string]: string };
  fieldValues: { [key: string]: string | number };
}

interface ExistingSegment {
  id: string;
  name: string;
  type: string;
  description?: string | null;
  rulesJson: any[];
}

// ==================== OPERATOR OPTIONS ====================
const OPERATOR_OPTIONS = [
  'EQUALS',
  'NOT_EQUALS',
  'GT',
  'LT',
  'GTE',
  'LTE',
  'IN',
  'IS_NOT_NULL',
  'MATCH_ALL',
  'NOT_IN',
  'EXISTS',
  'BETWEEN',
  'CONTAINS'
];

// Operators that don't require a value
const OPERATORS_WITHOUT_VALUE = ['IS_NOT_NULL', 'EXISTS'];

// ==================== MAP INPUT TYPE TO VALUE ====================
const getRuleValueFromInputType = (inputType: string): string => {
  switch (inputType) {
    case 'Number':
      return 'Number';
    case 'Calendar':
      return 'Calendar';
    case 'Dropdown':
      return 'Dropdown';
    case 'Text':
    default:
      return 'Text';
  }
};

type NameCheckStatus = 'idle' | 'checking' | 'available' | 'taken';

const NewSegmentForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [existingSegments, setExistingSegments] = useState<ExistingSegment[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ==================== DEBOUNCED NAME CHECK STATE ====================
  const [nameCheckStatus, setNameCheckStatus] = useState<NameCheckStatus>('idle');
  const nameCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Used to ignore responses from stale requests (user typed again before response arrived)
  const latestCheckIdRef = useRef(0);

  const availableFields = [
    'Name',
    'Email',
    'Contact',
    'Gender',
    'DOB',
    'Location',
  ];

  // ==================== FETCH EXISTING SEGMENTS (initial load, used for validation) ====================
  const fetchExistingSegments = async () => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;

    try {
      const response = await fetch(`${BASE_URL}/v1/audience-insights/segments`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const mappedSegments = (data.content || []).map((seg: any) => ({
        id: seg.id,
        name: seg.name,
        type: seg.type,
        description: seg.description,
        rulesJson: seg.rulesJson || [],
      }));

      setExistingSegments(mappedSegments);
    } catch (error) {
      // console.error('Failed to fetch existing segments:', error);
      toast.error('Failed to load existing segments. Name validation may not work correctly.');
    }
  };

  useEffect(() => {
    fetchExistingSegments();
  }, []);

  // ==================== CHECK NAME VIA API ====================
  // Called after the 1s debounce. Fetches all segments and checks for a match.
  const checkSegmentNameAvailability = async (nameToCheck: string) => {
    const trimmed = nameToCheck.trim();
    if (!trimmed) {
      setNameCheckStatus('idle');
      return;
    }

    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      setNameCheckStatus('idle');
      return;
    }

    // Increment the check id so we can ignore stale responses
    const checkId = ++latestCheckIdRef.current;

    setNameCheckStatus('checking');

    try {
      const response = await fetch(
        `${BASE_URL}/v1/audience-insights/segments?page=0&size=1000`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      // Ignore stale response — user typed again while we were waiting
      if (checkId !== latestCheckIdRef.current) return;

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const segmentsFromApi: ExistingSegment[] = (data.content || []).map((seg: any) => ({
        id: seg.id,
        name: seg.name,
        type: seg.type,
        description: seg.description,
        rulesJson: seg.rulesJson || [],
      }));

      // Keep the existing-segments list in sync for Formik's validation too
      setExistingSegments(segmentsFromApi);

      const exists = segmentsFromApi.some(
        (s) => s.name.toLowerCase() === trimmed.toLowerCase()
      );

      if (checkId !== latestCheckIdRef.current) return;
      setNameCheckStatus(exists ? 'taken' : 'available');
    } catch (error) {
      // console.error('❌ Name availability check failed:', error);
      toast.error('Failed to check segment name availability. Please try again later.');
      if (checkId !== latestCheckIdRef.current) return;
      setNameCheckStatus('idle');
    }
  };

  // ==================== DEBOUNCE THE NAME INPUT (1s) ====================
  const handleNameInputChange = (value: string) => {
    // Show "checking" immediately so the user gets feedback
    if (value.trim()) {
      setNameCheckStatus('checking');
    } else {
      setNameCheckStatus('idle');
    }

    // Clear the previous timer
    if (nameCheckTimeoutRef.current) {
      clearTimeout(nameCheckTimeoutRef.current);
    }

    // Set a new 1-second timer that fires the API check
    nameCheckTimeoutRef.current = setTimeout(() => {
      checkSegmentNameAvailability(value);
    }, 1000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (nameCheckTimeoutRef.current) {
        clearTimeout(nameCheckTimeoutRef.current);
      }
    };
  }, []);

  const getFieldTypeOptions = (field: string) => {
    if (field === 'DOB') return ['Calendar', 'Text'];
    if (field === 'Gender') return ['Dropdown', 'Text'];
    return ['Text', 'Number'];
  };

  const validationSchema = Yup.object({
    segmentName: Yup.string()
      .required('Segment name is required')
      .min(2, 'Segment name must be at least 2 characters')
      .test('unique-name', 'Name unavailable, please choose a different name', function (value) {
        if (!value) return true;
        const nameExists = existingSegments.some(
          segment => segment.name.toLowerCase() === value.trim().toLowerCase()
        );
        if (nameExists) {
          return this.createError({ message: 'Name unavailable, please choose a different name' });
        }
        return true;
      }),
    selectedFields: Yup.array().of(Yup.string()).when('segmentType', {
      is: 'DYNAMIC',
      then: (schema) => schema.min(1, 'Select at least one field'),
      otherwise: (schema) => schema.notRequired(),
    }),
    logicalOperator: Yup.string().when('segmentType', {
      is: 'DYNAMIC',
      then: (schema: Yup.StringSchema) => schema.required('Logical operator is required'),
      otherwise: (schema: Yup.StringSchema) => schema.notRequired(),
    }),
  });

  const initialValues: FormValues = {
    segmentName: '',
    segmentType: 'DYNAMIC',
    logicalOperator: 'AND',
    selectedFields: [],
    fieldTypes: {},
    fieldOperators: {},
    fieldValues: {},
  };

  const buildRulesJson = (
    selectedFields: string[],
    fieldTypes: { [key: string]: string },
    fieldOperators: { [key: string]: string },
    fieldValues: { [key: string]: string | number },
    logicalOperator: string
  ) => {
    if (selectedFields.length === 0) return [];

    const rules = selectedFields.map((field) => {
      let apiField = field.toLowerCase().replace(/\s+/g, '');
      if (field === 'Name') apiField = 'name';
      if (field === 'Contact') apiField = 'phone';

      const operator = fieldOperators[field] || 'EQUALS';
      let value = fieldValues[field] || '';

      if (!value && !OPERATORS_WITHOUT_VALUE.includes(operator)) {
        value = getRuleValueFromInputType(fieldTypes[field] || 'Text');
      }

      return {
        type: "ATTRIBUTE",
        field: apiField,
        operator: operator,
        value: String(value)
      };
    });

    return [
      {
        type: logicalOperator,
        rules: rules
      }
    ];
  };

  const createSegmentAPI = async (values: FormValues) => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      toast.error('Authentication token not found');
      return null;
    }

    let payload: any = {
      name: values.segmentName,
      type: values.segmentType,
    };

    if (values.segmentType === 'DYNAMIC') {
      payload.rulesJson = buildRulesJson(
        values.selectedFields,
        values.fieldTypes,
        values.fieldOperators,
        values.fieldValues,
        values.logicalOperator
      );
    }


    try {
      const response = await fetch(`${BASE_URL}/v1/audience-insights/segments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create segment');
      }

      const result = await response.json();
      toast.success('Segment created successfully!');
      return result;
    } catch (error: any) {
      // console.error('Create segment failed:', error);
      toast.error(error.message || 'Failed to create segment');
      return null;
    }
  };

  const handleAddNewField = (setFieldValue: any, values: FormValues) => {
    if (newFieldName.trim()) {
      const field = newFieldName.trim();
      if (!values.selectedFields.includes(field)) {
        setFieldValue('selectedFields', [...values.selectedFields, field]);
        setFieldValue('fieldTypes', {
          ...values.fieldTypes,
          [field]: getFieldTypeOptions(field)[0],
        });
        setFieldValue('fieldOperators', {
          ...values.fieldOperators,
          [field]: 'EQUALS',
        });
        setFieldValue('fieldValues', {
          ...values.fieldValues,
          [field]: field === 'Gender' ? 'Male' : '',
        });
      }
      setNewFieldName('');
    }
  };

  const handleDeleteField = (setFieldValue: any, values: FormValues, field: string) => {
    setFieldValue('selectedFields', values.selectedFields.filter((f) => f !== field));
    const newFieldTypes = { ...values.fieldTypes };
    delete newFieldTypes[field];
    setFieldValue('fieldTypes', newFieldTypes);

    const newFieldOperators = { ...values.fieldOperators };
    delete newFieldOperators[field];
    setFieldValue('fieldOperators', newFieldOperators);

    const newFieldValues = { ...values.fieldValues };
    delete newFieldValues[field];
    setFieldValue('fieldValues', newFieldValues);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Render value field based on field type
  const renderValueField = (
    field: string,
    fieldValue: string | number,
    requiresValue: boolean
  ) => {
    if (!requiresValue) {
      return (
        <Field
          name={`fieldValues.${field}`}
          placeholder="Not required"
          disabled={true}
          className="w-full border border-gray-300 rounded-full px-4 py-2 text-sm bg-gray-100 text-gray-500 focus:outline-none"
        />
      );
    }

    if (field === 'Gender') {
      return (
        <Field
          as="select"
          name={`fieldValues.${field}`}
          className="w-full border border-gray-300 rounded-full px-4 py-2 text-sm text-[#3F5265] bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </Field>
      );
    }

    if (field === 'DOB') {
      return (
        <Field
          type="date"
          name={`fieldValues.${field}`}
          className="w-full border border-gray-300 rounded-full px-4 py-2 text-sm text-[#3F5265] bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      );
    }

    if (field === 'Contact') {
      return (
        <Field
          type="number"
          name={`fieldValues.${field}`}
          placeholder="Enter phone number *"
          className="w-full border border-gray-300 rounded-full px-4 py-2 text-sm text-[#3F5265] shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      );
    }

    return (
      <Field
        name={`fieldValues.${field}`}
        placeholder="Enter value *"
        className="w-full border border-gray-300 rounded-full px-4 py-2 text-sm text-[#3F5265] shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
      />
    );
  };

  return (
    <div className='p-2'>
      <button
        className='flex items-center text-sm text-teal-600 mb-4 hover:underline font-semibold'
        style={{ color: '#00899E', fontSize: '14px' }}
        onClick={() => navigate('/segments')}
      >
        <FiChevronLeftComponent className='mr-1' /> Back to Segments
      </button>

      <h2 className='text-2xl font-bold text-[#3F5265] text-[24px]'>New Segment</h2>
      <p className='text-[#657584] text-[16px]'>
        Create new segment by giving a proper name and select filters you want in it.
      </p>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          try {
            const apiResult = await createSegmentAPI(values);
            if (apiResult) {
              const segmentConfig: SegmentConfig = {
                name: values.segmentName.trim(),
                fields: values.selectedFields,
                fieldTypes: values.fieldTypes,
              };
              dispatch(addSegment(segmentConfig));
              setTimeout(() => navigate('/segments'), 1200);
            }
          } catch (error) {
            // console.error('Submit error:', error);
            toast.error('Failed to create segment. Please try again later.');
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, setFieldValue, isValid, dirty, errors, touched, isSubmitting, submitForm }) => {
          const isDynamic = values.segmentType === 'DYNAMIC';

          const areAllValuesFilled = values.selectedFields.every((field) => {
            const operator = values.fieldOperators[field] || 'EQUALS';
            if (OPERATORS_WITHOUT_VALUE.includes(operator)) {
              return true;
            }
            const value = values.fieldValues[field];
            return value !== undefined && value !== null && String(value).trim() !== '';
          });

          const isFormValid = isDynamic
            ? values.segmentName.trim() !== '' &&
            !errors.segmentName &&
            nameCheckStatus !== 'taken' &&
            values.selectedFields.length > 0 &&
            areAllValuesFilled
            : values.segmentName.trim() !== '' &&
            !errors.segmentName &&
            nameCheckStatus !== 'taken';

          const hasFieldValueErrors = values.selectedFields.some((field) => {
            const operator = values.fieldOperators[field] || 'EQUALS';
            if (OPERATORS_WITHOUT_VALUE.includes(operator)) {
              return false;
            }
            const value = values.fieldValues[field];
            return value === undefined || value === null || String(value).trim() === '';
          });

          return (
            <Form className='bg-white p-6 rounded-lg shadow-sm border border-gray-200 mt-6'>
              <div className='grid grid-cols-2 gap-4 mb-6'>
                <div>
                  <label className='block mb-2 font-semibold text-[#3F5265] text-[12px]'>
                    SEGMENT NAME
                  </label>
                  <Field
                    name='segmentName'
                    className={`w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 ${errors.segmentName && touched.segmentName
                      ? 'border-red-500'
                      : nameCheckStatus === 'taken'
                        ? 'border-red-500'
                        : nameCheckStatus === 'available'
                          ? 'border-green-500'
                          : 'border-gray-300'
                      }`}
                    placeholder='Enter segment name'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('segmentName', e.target.value);
                      handleNameInputChange(e.target.value);
                    }}
                  />

                  {/* Formik errors (required / min) */}
                  <ErrorMessage name='segmentName' component='div' className='text-red-500 text-xs mt-1' />

                  {/* Debounced API-check feedback */}
                  {nameCheckStatus === 'checking' && (
                    <div className='flex items-center gap-2 text-xs mt-1 text-gray-500'>
                      <span className='inline-block w-3 h-3 border-2 border-t-transparent border-gray-400 rounded-full animate-spin'></span>
                      Checking availability...
                    </div>
                  )}

                  {nameCheckStatus === 'available' && !errors.segmentName && (
                    <div className='flex items-center gap-1 text-xs mt-1 text-green-600'>
                      <FiCheckComponent size={14} />
                      Name is available
                    </div>
                  )}

                  {nameCheckStatus === 'taken' && !errors.segmentName && (
                    <div className='flex items-center gap-1 text-xs mt-1 text-red-500'>
                      <FiAlertCircleComponent size={14} />
                      This name already exists, please choose a different one
                    </div>
                  )}
                </div>

                <div>
                  <div className='flex items-center gap-4'>
                    <div className='flex-1'>
                      <label className='block mb-2 font-semibold text-[#3F5265] text-[12px]'>
                        SEGMENT TYPE
                      </label>
                      <Field
                        as="select"
                        name="segmentType"
                        className='w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                        onChange={(e: any) => {
                          const newType = e.target.value;
                          setFieldValue('segmentType', newType);
                          if (newType === 'STATIC') {
                            setFieldValue('selectedFields', []);
                            setFieldValue('fieldTypes', {});
                            setFieldValue('fieldOperators', {});
                            setFieldValue('fieldValues', {});
                            setFieldValue('logicalOperator', 'AND');
                          }
                        }}
                      >
                        <option value="DYNAMIC">Dynamic Segment</option>
                        <option value="STATIC">Static Segment</option>
                      </Field>
                    </div>
                    {isDynamic && (
                      <div className='flex-1'>
                        <label className='block mb-2 font-semibold text-[#3F5265] text-[12px]'>
                          LOGICAL OPERATOR
                        </label>
                        <Field
                          as="select"
                          name="logicalOperator"
                          className='w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                        >
                          <option value="AND">AND</option>
                          <option value="OR">OR</option>
                        </Field>
                        <ErrorMessage name='logicalOperator' component='div' className='text-red-500 text-xs mt-1' />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Fields Selection - Only shown for DYNAMIC segments */}
              {isDynamic && (
                <>
                  <div className='relative mb-6' ref={dropdownRef}>
                    <label className='block mb-2 font-semibold text-[#3F5265] text-[12px]'>
                      SELECT FILTERS
                    </label>

                    <div
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className='w-full min-h-[46px] border border-gray-300 px-3 py-2 rounded cursor-pointer bg-gray-50 flex items-center justify-between gap-2 flex-wrap'
                    >
                      <div className='flex flex-wrap gap-2 items-center flex-1'>
                        {values.selectedFields.length === 0 ? (
                          <span className='text-gray-400 text-sm'>
                            Click to select filters
                          </span>
                        ) : (
                          values.selectedFields.map((field) => (
                            <span
                              key={field}
                              className='inline-flex items-center gap-2 bg-[#E8EFF5] text-[#3F5265] text-sm font-medium rounded-full pl-4 pr-2 py-1.5'
                            >
                              {field}
                              <button
                                type='button'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteField(setFieldValue, values, field);
                                }}
                                className='text-[#3F5265] hover:text-red-500 rounded-full p-0.5'
                                aria-label={`Remove ${field}`}
                              >
                                <FiXComponent size={14} />
                              </button>
                            </span>
                          ))
                        )}
                      </div>

                      <FiChevronDownComponent
                        className={`text-gray-500 transition-transform shrink-0 ${isDropdownOpen ? 'rotate-180' : ''
                          }`}
                      />
                    </div>

                    {isDropdownOpen && (
                      <div className='absolute bg-white border border-gray-300 rounded shadow-md p-4 z-10 w-3/4 mt-1 right-0'>
                        <div className='mb-4'>
                          <input
                            value={newFieldName}
                            onChange={(e) => setNewFieldName(e.target.value)}
                            placeholder='New field'
                            className='w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                          />
                          <button
                            type='button'
                            disabled={!newFieldName.trim()}
                            className={`mt-2 px-4 py-2 rounded font-medium ${newFieldName.trim()
                              ? 'bg-teal-600 text-white hover:bg-teal-700'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              }`}
                            onClick={() => handleAddNewField(setFieldValue, values)}
                          >
                            + Add new field
                          </button>
                        </div>

                        <div className='space-y-2'>
                          {availableFields.map((field) => (
                            <label key={field} className='flex items-center cursor-pointer'>
                              <input
                                type='checkbox'
                                checked={values.selectedFields.includes(field)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFieldValue('selectedFields', [...values.selectedFields, field]);
                                    setFieldValue('fieldTypes', {
                                      ...values.fieldTypes,
                                      [field]: getFieldTypeOptions(field)[0],
                                    });
                                    setFieldValue('fieldOperators', {
                                      ...values.fieldOperators,
                                      [field]: 'EQUALS',
                                    });
                                    setFieldValue('fieldValues', {
                                      ...values.fieldValues,
                                      [field]: field === 'Gender' ? 'Male' : '',
                                    });
                                  } else {
                                    handleDeleteField(setFieldValue, values, field);
                                  }
                                }}
                                className='w-4 h-4 text-teal-600 border-gray-300 rounded'
                              />
                              <span className={`ml-2 text-[14px] text-[#3F5265]`}>
                                {field}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <ErrorMessage name='selectedFields' component='div' className='text-red-500 text-xs mt-1' />
                  </div>

                  {/* Rule rows - styled as compact pills like the design */}
                  {values.selectedFields.length > 0 && (
                    <div className='mb-6 space-y-3'>
                      {values.selectedFields.map((field) => {
                        const operator = values.fieldOperators[field] || 'EQUALS';
                        const requiresValue = !OPERATORS_WITHOUT_VALUE.includes(operator);
                        const value = values.fieldValues[field];
                        const hasValueError =
                          requiresValue &&
                          (value === undefined ||
                            value === null ||
                            String(value).trim() === '');

                        return (
                          <div
                            key={field}
                            className='flex items-start gap-3 flex-wrap bg-[#F4FEFF] border border-[#E0F8FB] rounded-[10px] px-4 py-3'
                          >
                            {/* Field name pill */}
                            <div className='relative'>
                              <select
                                value={field}
                                disabled
                                className='appearance-none bg-white border border-gray-300 rounded-full pl-4 pr-4 py-2 text-sm font-medium text-[#3F5265] shadow-sm focus:outline-none'
                              >
                                <option>{field}</option>
                              </select>
                              {/* <FiChevronDownComponent
                                size={14}
                                className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500'
                              /> */}
                            </div>

                            {/* Operator pill */}
                            <div className='relative'>
                              <Field
                                as='select'
                                name={`fieldOperators.${field}`}
                                className='appearance-none bg-white border border-gray-300 rounded-full pl-4 pr-9 py-2 text-sm font-medium text-[#3F5265] shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer'
                                onChange={(e: any) => {
                                  const newOperator = e.target.value;
                                  setFieldValue(`fieldOperators.${field}`, newOperator);
                                  if (OPERATORS_WITHOUT_VALUE.includes(newOperator)) {
                                    setFieldValue(`fieldValues.${field}`, '');
                                  }
                                }}
                              >
                                {OPERATOR_OPTIONS.map((op) => (
                                  <option key={op} value={op}>
                                    {op}
                                  </option>
                                ))}
                              </Field>
                              <FiChevronDownComponent
                                size={14}
                                className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500'
                              />
                            </div>

                            {/* Value group */}
                            <div className='flex items-center flex-1 min-w-[260px]'>
                              <div className='flex-1'>
                                {renderValueField(field, value, requiresValue)}
                              </div>
                            </div>

                            {/* Remove button */}
                            <button
                              type='button'
                              onClick={() => handleDeleteField(setFieldValue, values, field)}
                              className='text-red-400 hover:text-red-600 mt-2'
                            >
                              <FiXComponent size={18} />
                            </button>

                            {hasValueError && (
                              <div className='w-full text-red-500 text-xs mt-1 pl-1'>
                                Value is required
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* {hasFieldValueErrors && (
                        <div className='text-red-500 text-xs mt-2'>
                          Please enter a value for all fields that require it
                        </div>
                      )} */}
                    </div>
                  )}
                </>
              )}

              {/* Static segment info message */}
              {!isDynamic && (
                <div className='mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md'>
                  <p className='text-blue-700 text-sm'>
                    <strong>Static Segment:</strong> This segment type doesn't require any rules or field configurations.
                    You can manually add users to this segment later.
                  </p>
                </div>
              )}

              <div className='flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200'>
                <button
                  type='button'
                  onClick={() => navigate('/segments')}
                  className='px-6 py-2 bg-orange-500 text-white rounded font-medium hover:bg-orange-600'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={!isFormValid || isSubmitting}
                  className={`px-6 py-2 rounded text-white font-medium ${isFormValid && !isSubmitting
                    ? 'bg-teal-600 hover:bg-teal-700'
                    : 'bg-gray-300 cursor-not-allowed'
                    }`}
                >
                  {isSubmitting ? 'Saving...' : 'Save & Continue'}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
      <ToastContainer position='top-right' autoClose={3000} />
    </div>
  );
};

export default NewSegmentForm;